import { ProfilePagesUpdateMessage, useUpdateBroadcastChannel } from '@mvecom/common/hooks/useUpdateBroadcastChannel';
import { useEffect, useCallback, useMemo, useState, useRef } from 'react';

import { getAuth0Session } from '../network/endpoints/profile-pages/getAuth0Session';
import { resetSsoCheckCooldown } from '../network/endpoints/profile-pages/getVerifiedAuthStatus';
import { getRetailUnitAndLanguage } from '../network/utils/getRetailUnitAndLanguage';
import { getAuthServiceUrl, toUrl } from '../utils/url';

export type Auth0Event =
  | { status: 'initial-auth-check-pending' }
  | { status: 'initial-auth-check-true' }
  | { status: 'initial-auth-check-false'; error?: Error }
  | { status: 'log-in-attempt-success' }
  | { status: 'log-out-attempt-success' };

export function logoutFromAuth0() {
  const { retailUnit, language: lang } = getRetailUnitAndLanguage();
  globalThis.location.href = toUrl(`${getAuthServiceUrl()}/logout`, {
    lang,
    retailUnit,
    referrer: globalThis.location.href,
  });
}

export default function useAuth0(enabled: boolean, onAuthEvent: (event: Auth0Event) => void) {
  const { retailUnit, language: lang } = getRetailUnitAndLanguage();
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [authCheckAttempt, setAuthCheckAttempt] = useState(0);
  const onAuthEventRef = useRef(onAuthEvent);
  useEffect(() => {
    onAuthEventRef.current = onAuthEvent;
  }, [onAuthEvent, onAuthEventRef]);

  const logout = useCallback(() => {
    globalThis.location.href = toUrl(`${getAuthServiceUrl()}/logout`, {
      lang,
      retailUnit,
      referrer: globalThis.location.href,
    });
  }, [lang, retailUnit]);

  const login = useCallback(() => {
    globalThis.location.href = toUrl(`${getAuthServiceUrl()}/login`, {
      lang,
      prompt: 'login',
      retailUnit,
      referrer: globalThis.location.href,
    });
  }, [lang, retailUnit]);

  const retry = useCallback(() => {
    resetSsoCheckCooldown();
    setAuthCheckAttempt((attempt) => attempt + 1);
  }, []);

  useUpdateBroadcastChannel(
    'profile_pages_update',
    retailUnit,
    useCallback(
      (message: ProfilePagesUpdateMessage) => {
        if (!enabled) {
          return;
        }
        if (message.action === 'signed-in') {
          onAuthEventRef.current({ status: 'log-in-attempt-success' });
          setLoggedIn(true);
        }

        if (message.action === 'signed-out') {
          onAuthEventRef.current({ status: 'log-out-attempt-success' });
          setLoggedIn(false);
        }
      },
      [onAuthEventRef, enabled],
    ),
  );

  // initial auth check
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const getSessionStatus = async (abortController: AbortController) => {
      onAuthEventRef.current({ status: 'initial-auth-check-pending' });

      try {
        const sessionStatus = await getAuth0Session(abortController.signal);

        if (!abortController.signal.aborted) {
          setLoggedIn(sessionStatus.isAuthenticated);
          onAuthEventRef.current({
            status: sessionStatus.isAuthenticated ? 'initial-auth-check-true' : 'initial-auth-check-false',
          });
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error(error);
          onAuthEventRef.current({
            status: 'initial-auth-check-false',
            error: error instanceof Error ? error : new Error('Auth0 SSO status check failed'),
          });
        }
      }
    };

    const abortController = new AbortController();
    getSessionStatus(abortController);

    return () => abortController.abort();
  }, [authCheckAttempt, enabled, onAuthEventRef]);

  return useMemo(() => ({ loggedIn, login, logout, retry }), [loggedIn, login, logout, retry]);
}
